import { RouteObject } from "react-router";
import { Navigate } from "react-router-dom";

import { AuthLayout } from "@covertree/layouts";

import { AUTH_ROUTES, BASE_AUTH_PATH } from "./paths";

import { ProtectedRoute } from "../ProtectedRoute";

import ConfirmSignIn from "./ConfirmSignIn";
import CreateNewPassword from "./CreateNewPassword";
import CreateNewPasswordSuccess from "./CreateNewPasswordSuccess";
import FederatedSignIn from "./FederatedLogIn";
import ForgotPassword from "./ForgotPassword";
import Login from "./Login";
import MfaSetup from "./MfaSetup";
import CheckEmail from "./CheckEmail";

interface AuthPageSettings {
  authLayoutSettings?: {
    title?: string;
    subtitle?: string;
  };
  title: string;
}

interface AuthRoutesParams {
  confirmSignInPage?: AuthPageSettings;
  loginPage: AuthPageSettings;
  mfaPage?: AuthPageSettings;
  useManualForgotPassword?: boolean;
  ssoProvidersList: string[];
  isPasswordless: boolean;
  isMFAEnabled: boolean;
  whenLoggedIn: (groups: string[]) => string;
  logo: React.ReactNode;
}

export const createAuthRoutes = ({
  confirmSignInPage: {
    title: confirmSignInTitle,
    authLayoutSettings: confirmSignInPageLayoutSettings,
  } = {
    title: "Confirm Sign In",
  },
  loginPage: { authLayoutSettings, title: loginTitle },
  mfaPage: { title: mfaTitle } = { title: "MFA Setup" },
  useManualForgotPassword = false,
  ssoProvidersList,
  isPasswordless,
  isMFAEnabled,
  whenLoggedIn,
  logo,
}: AuthRoutesParams): RouteObject[] => [
  {
    path: BASE_AUTH_PATH,
    element: (
      <ProtectedRoute shouldBeLoggedIn={false} whenNotAllowed={whenLoggedIn} />
    ),
    children: [
      // Make sure this one does not include AuthLayout
      {
        path: AUTH_ROUTES.CREATE_NEW_PASSWORD_SUCCESS,
        element: <CreateNewPasswordSuccess />,
      },
      ...(useManualForgotPassword
        ? [
            {
              path: AUTH_ROUTES.CHECK_EMAIL,
              element: <CheckEmail />,
            },
          ]
        : []),
      {
        path: "*",
        element: <AuthLayout loginPath={AUTH_ROUTES.LOGIN} logo={logo} />,
        children: [
          {
            index: true,
            path: AUTH_ROUTES.LOGIN,
            handle: {
              title: authLayoutSettings?.title ?? "Sign in to Covertree",
              subtitle: isPasswordless
                ? "Enter your phone number. We will send you a message with a login code."
                : authLayoutSettings?.subtitle ?? undefined,
            },
            element: (
              <Login
                isPasswordless={isPasswordless}
                title={loginTitle}
                forgotPasswordPath={
                  isPasswordless ? undefined : AUTH_ROUTES.FORGOT_PASSWORD
                }
              />
            ),
          },
          {
            path: AUTH_ROUTES.CONFIRM_SIGN_IN,
            handle: {
              canGoBack: true,
              title:
                confirmSignInPageLayoutSettings?.title ??
                "Enter the verification code",
              subtitle:
                confirmSignInPageLayoutSettings?.subtitle ?? isPasswordless
                  ? "A code was sent to your phone number. Please enter your verification code below."
                  : "Please enter your verification code below.",
            },
            element: <ConfirmSignIn title={confirmSignInTitle} />,
          },
          ...(!isPasswordless
            ? [
                {
                  path: AUTH_ROUTES.CREATE_NEW_PASSWORD,
                  handle: {
                    canGoBack: true,
                    title: "Create a new password",
                  },
                  element: <CreateNewPassword />,
                },
                {
                  path: AUTH_ROUTES.FORGOT_PASSWORD,
                  handle: {
                    canGoBack: true,
                    title: "Forgot your password?",
                    subtitle:
                      "Enter your email address below to receive an email with a link to reset your password.",
                  },
                  element: (
                    <ForgotPassword
                      useManualForgotPassword={useManualForgotPassword}
                    />
                  ),
                },
              ]
            : []),
          ...(ssoProvidersList.length > 0
            ? [
                {
                  path: AUTH_ROUTES.SSO_LOGIN,
                  handle: {
                    title: "Sign in to Covertree with SSO",
                  },
                  element: (
                    <FederatedSignIn allowedOauthProviders={ssoProvidersList} />
                  ),
                },
              ]
            : []),
          ...(isMFAEnabled
            ? [
                {
                  path: AUTH_ROUTES.MFA_SETUP,
                  handle: {
                    title: "Setup Multi Factor Authentication",
                    subtitle:
                      "We recommend Microsoft Authenticator or Google Authenticator",
                  },
                  element: <MfaSetup title={mfaTitle} />,
                },
              ]
            : []),
          {
            path: "*",
            element: <Navigate to={AUTH_ROUTES.LOGIN} />,
          },
        ],
      },
    ],
  },
];

export * from "./paths";
